/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("WindowNode", ["StandardNode", "NodeFactory"], function (context, cls) {
  /**
   * @class WindowNode
   * @memberOf classes
   * @extends classes.StandardNode
   */
  class WindowNode extends cls.StandardNode /** @lends classes.WindowNode.prototype */ {
    /**
     * @inheritDoc
     */
    constructor(parent, tag, id, attributes, app) {
      super(parent, tag, id, attributes, app);

      /** @type {string} */
      this._title = "";
      /** @type {string} */
      this._icon = "";
      /** @type {null|number} */
      this._parentWindowId = null;
      /** @type {boolean} */
      this._isModal = false;
      /** @type {boolean} */
      this._isDestroying = false;
    }

    /**
     * @inheritDoc
     */
    destroy() {
      this._isDestroying = true;
      super.destroy();
    }

    /**
     * Get the window title
     * @returns {string}
     */
    getTitle() {
      return this._title;
    }

    /**
     * Set the window title
     * @param {string} title
     */
    setTitle(title) {
      this._title = title;
      this.emit(context.constants.widgetEvents.titleChanged, title);
    }

    /**
     * Get the window icon
     * @returns {string}
     */
    getIcon() {
      return this._icon;
    }

    /**
     * Set the window icon
     * @param {string} icon
     */
    setIcon(icon) {
      this._icon = icon;
      const widget = this.getWidget();
      if (widget) {
        widget.setImage(icon);
      }
      this.emit(context.constants.widgetEvents.iconChanged, icon);
    }

    /**
     * Return whether this window is the current one in the application or not
     * @returns {boolean}
     */
    isCurrentWindowNode() {
      const uiNode = this.getApplication().getNode(0);
      return uiNode.attribute("currentWindow") === this.getId();
    }

    /**
     * Is this window in traditional mode
     * @returns {boolean}
     */
    isTraditional() {
      const uiNode = this.getApplication().getNode(0);
      const winStyle = this.attribute("style");

      // Traditional mode is not applied on dialog (as GDC)
      if (uiNode.attribute("uiMode") === "traditional" && winStyle !== "dialog") {
        const formNode = this.getFirstChild("Form");
        if (formNode) {
          const screenNode = formNode.getFirstChild("Screen");
          if (screenNode) {
            return true;
          }
        }
      }
      return false;
    }

    /**
     * Get the first traditional window
     * @returns {null|classes.WindowNode}
     */
    getFirstTraditionalWindow() {
      const children = this.getApplication().getNode(0).getChildren();
      for (const child of children) {
        if (child.getTag() === "Window" && child.isTraditional()) {
          return child;
        }
      }
      return null;
    }

    /**
     * Get the active dialog
     * @returns {classes.NodeBase | null}
     */
    getActiveDialog() {
      for (const child of this._children) {
        const childIsActive = child.attribute("active") === 1;
        const childIsMenuOrDialog = child._tag === "Menu" || child._tag === "Dialog";
        if (childIsMenuOrDialog && childIsActive) {
          return child;
        }
      }
      return null;
    }

    /**
     * Is this window modal
     * @returns {boolean}
     */
    isModal() {
      if (this._isDestroying) {
        return this._isModal;
      }

      const windowTypeAttr = this.getStyleAttribute("windowType");
      this._isModal =
        windowTypeAttr === "modal" ||
        windowTypeAttr === "popup" ||
        (windowTypeAttr === "modalOnLargeScreen" && gbc.ThemeService.getMediaString() === "large");
      return this._isModal;
    }

    _setProcessingStyle(processing) {
      const widget = this._controller && this._controller.getWidget();
      if (widget?._setProcessingStyle) {
        widget._setProcessingStyle(processing);
      }
    }

    /**
     * Set the parent window identifier
     * @param {number} parentWindowId identifier o17f the parent window
     */
    setParentWindowId(parentWindowId) {
      this._parentWindowId = parentWindowId;
    }

    /**
     * Get the parent window identifier
     * @return {number} - the id of the parent window
     */
    getParentWindowId() {
      return this._parentWindowId;
    }

    /**
     * Get the pasent window when modal
     * @returns {null|classes.WindowNode}
     */
    getParentNodeWhenModal() {
      if (this.isModal() && this.getParentWindowId() > 0) {
        return this.getParentNode().getFirstChildWithId(this.attribute("parent"));
      }
      return null;
    }
  }
  cls.WindowNode = WindowNode;
  cls.NodeFactory.register("Window", cls.WindowNode);
});
